/**
 * Mass Construction — Home section
 * Gestiona la activación de vídeos por scroll (desktop + mobile),
 * orientación en móvil y sincronización con el nav lateral.
 */
(function () {
  'use strict';

  const BREAKPOINT_MOBILE = 749;

  // Dimensiones del filmstrip desktop (deben coincidir con el CSS)
  const THUMB_H   = 150;  // altura del thumbnail inactivo
  const ACTIVE_H  = 430;  // altura del item activo
  const ITEM_GAP  = 24;   // gap entre items (gap en el track)
  const ANIM_MS   = 500;  // duración de la transición CSS

  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), a[href], input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** Drawer OWOW!! dentro del ítem expandido del home */
  class ItemDrawerController {
    constructor(itemEl) {
      this.item     = itemEl;
      this.drawer   = itemEl.querySelector('[data-mc-drawer]');
      this.trigger  = itemEl.querySelector('[data-mc-drawer-trigger]');
      this.panel    = this.drawer?.querySelector('.mc-drawer__panel');
      this.backdrop = this.drawer?.querySelector('[data-mc-drawer-backdrop]');
      this.isOpen   = false;
      this._prevFocus  = null;
      this._drawerOrigin = null;
      this._keyHandler = this._onKeyDown.bind(this);
      this._outsideHandler = this._onOutsidePointer.bind(this);

      if (!this.drawer || !this.panel) return;

      this.drawer.querySelectorAll('[data-mc-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });
      });

      this.trigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.open();
      });

      this.backdrop?.addEventListener('pointerdown', (e) => {
        if (e.target === this.backdrop) {
          e.stopPropagation();
          this.close();
        }
      });
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this._prevFocus = document.activeElement;

      this._setInert(true);
      this.item.classList.add('is-drawer-open');

      // Portal: sacar el drawer del ítem expandido para escapar overflow/stacking del vídeo
      this._drawerOrigin = {
        parent: this.drawer.parentNode,
        nextSibling: this.drawer.nextSibling,
      };
      document.body.appendChild(this.drawer);

      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.trigger?.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.panel.querySelector(FOCUSABLE_SELECTOR)?.focus({ preventScroll: true });
      });

      document.addEventListener('keydown', this._keyHandler, true);
      document.addEventListener('pointerdown', this._outsideHandler, true);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.item.classList.remove('is-drawer-open');

      if (this._drawerOrigin?.parent) {
        const { parent, nextSibling } = this._drawerOrigin;
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(this.drawer, nextSibling);
        } else {
          parent.appendChild(this.drawer);
        }
      }
      this._drawerOrigin = null;

      this._setInert(false);

      requestAnimationFrame(() => {
        (this._prevFocus || this.trigger)?.focus({ preventScroll: true });
        this._prevFocus = null;
      });

      document.removeEventListener('keydown', this._keyHandler, true);
      document.removeEventListener('pointerdown', this._outsideHandler, true);
    }

    _onOutsidePointer(e) {
      if (!this.isOpen) return;
      if (this.panel.contains(e.target)) return;
      this.close();
    }

    _setInert(inert) {
      const media = this.item.querySelector('[data-mc-item-media]');
      if (!media) return;
      if (inert) {
        media.setAttribute('inert', '');
        media.setAttribute('aria-hidden', 'true');
      } else {
        media.removeAttribute('inert');
        media.removeAttribute('aria-hidden');
      }
    }

    _getFocusable() {
      return [...this.panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (el) => !el.closest('[aria-hidden="true"]')
      );
    }

    _onKeyDown(e) {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = this._getFocusable();
      if (!focusable.length) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !this.panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !this.panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.close();
      document.removeEventListener('keydown', this._keyHandler, true);
      this._setInert(false);
    }
  }

  /** Panel modal derecho del home (botón +) */
  class PanelModalController {
    constructor(home) {
      this.home    = home;
      this.section = home.section;
      this.drawer  = this.section.querySelector('[data-mc-panel-modal]');
      this.trigger = this.section.querySelector('[data-mc-panel-modal-trigger]');
      this.panel   = this.drawer?.querySelector('[data-mc-panel-modal-panel]');
      this.backdrop = this.drawer?.querySelector('[data-mc-panel-modal-backdrop]');
      this.isOpen  = false;
      this._prevFocus = null;
      this._drawerOrigin = null;
      this._keyHandler = this._onKeyDown.bind(this);
      this._outsideHandler = this._onOutsidePointer.bind(this);

      if (!this.drawer || !this.panel) return;

      this.drawer.querySelectorAll('[data-mc-panel-modal-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });
      });

      this.trigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.isOpen ? this.close() : this.open();
      });

      this.backdrop?.addEventListener('pointerdown', (e) => {
        if (e.target === this.backdrop) {
          e.stopPropagation();
          this.close();
        }
      });
    }

    syncColorsFromItem(item) {
      if (!this.panel || !item) return;

      const bg   = item.dataset.mcDrawerBg;
      const text = item.dataset.mcDrawerText;

      if (bg) this.panel.style.backgroundColor = bg;
      if (text) {
        this.panel.style.color = text;
        this.drawer.style.setProperty('--mc-item-text', text);
        this.drawer.style.setProperty('--mc-accent', text);
      }
    }

    open() {
      if (this.isOpen || !this.drawer) return;
      this.isOpen = true;
      this._prevFocus = document.activeElement;

      const activeItem = this.home.items[this.home.activeIndex];
      this.syncColorsFromItem(activeItem);

      this.section.classList.add('is-panel-modal-open');

      this._drawerOrigin = {
        parent: this.drawer.parentNode,
        nextSibling: this.drawer.nextSibling,
      };
      document.body.appendChild(this.drawer);

      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.trigger?.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.panel.querySelector(FOCUSABLE_SELECTOR)?.focus({ preventScroll: true });
      });

      document.addEventListener('keydown', this._keyHandler, true);
      document.addEventListener('pointerdown', this._outsideHandler, true);
    }

    close() {
      if (!this.isOpen || !this.drawer) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.section.classList.remove('is-panel-modal-open');

      if (this._drawerOrigin?.parent) {
        const { parent, nextSibling } = this._drawerOrigin;
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(this.drawer, nextSibling);
        } else {
          parent.appendChild(this.drawer);
        }
      }
      this._drawerOrigin = null;

      requestAnimationFrame(() => {
        (this._prevFocus || this.trigger)?.focus({ preventScroll: true });
        this._prevFocus = null;
      });

      document.removeEventListener('keydown', this._keyHandler, true);
      document.removeEventListener('pointerdown', this._outsideHandler, true);
    }

    _onOutsidePointer(e) {
      if (!this.isOpen) return;
      if (this.panel.contains(e.target)) return;
      if (e.target === this.trigger || this.trigger?.contains(e.target)) return;
      this.close();
    }

    _onKeyDown(e) {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (el) => !el.closest('[aria-hidden="true"]')
      );
      if (!focusable.length) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !this.panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !this.panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.close();
      document.removeEventListener('keydown', this._keyHandler, true);
      document.removeEventListener('pointerdown', this._outsideHandler, true);
    }
  }

  class MassConstructionHome {
    constructor(sectionEl) {
      this.section   = sectionEl;
      this.container = sectionEl.querySelector('[data-mc-videos]');
      this.track     = sectionEl.querySelector('[data-mc-track]');
      this.items     = [...sectionEl.querySelectorAll('[data-mc-item]')];
      this.navItems  = [...sectionEl.querySelectorAll('[data-mc-nav]')];
      this.navLinks  = [...sectionEl.querySelectorAll('[data-mc-nav-link]')];

      this.activeIndex      = -1;
      this._observer        = null;
      this._scrollTimer     = null;
      this._scrollLock      = false;
      this._scrollLockTimer = null;
      this._wheelLock       = false;
      this._expandedOrigin  = null;
      this._popStateHandler = this._onPopState.bind(this);
      this._homeUrl         = this._getHomeUrl();
      this._mqOrientation   = window.matchMedia('(orientation: landscape)');
      this._mqMobile        = window.matchMedia(`(max-width: ${BREAKPOINT_MOBILE}px)`);

      if (!this.items.length) return;

      this._bindNavLinks();
      this._bindOrientation();
      this._bindShopifyEvents();
      this._bindExpandClick();
      this._bindHistory();

      this.panelModal = new PanelModalController(this);

      if (this._isMobile()) {
        this._initMobileScroll();
      } else {
        this._initDesktopCarousel();
      }

      this._setActive(0);
      this._openInitialVideoFromUrl();
    }

    // ── Detección ────────────────────────────────────────────────────────────

    _isMobile() {
      return this._mqMobile.matches;
    }

    /** En móvil: logo, nav, panel y botón + usan el color de texto del ítem activo */
    _syncMobileUIColors(item) {
      const DESKTOP_UI_COLOR = '#000000';

      if (!this._isMobile()) {
        this.section.style.removeProperty('--mc-mobile-ui-color');
        return;
      }

      const color = item?.dataset?.mcDrawerText || DESKTOP_UI_COLOR;
      this.section.style.setProperty('--mc-mobile-ui-color', color);
    }

    /** Devuelve el elemento <video> correcto según plataforma y orientación */
    _getVideoEl(item) {
      if (!this._isMobile()) {
        return item.querySelector('[data-mc-video="desktop"]');
      }

      const isLandscape = this._mqOrientation.matches;
      const portrait    = item.querySelector('[data-mc-video="mobile-portrait"]');
      const landscape   = item.querySelector('[data-mc-video="mobile-landscape"]');
      const desktop     = item.querySelector('[data-mc-video="desktop"]');

      if (isLandscape)  return landscape || desktop || portrait;
      return portrait || desktop || landscape;
    }

    // ── Activación ───────────────────────────────────────────────────────────

    _pauseAll() {
      this.items.forEach(item => {
        item.querySelectorAll('video').forEach(v => {
          if (!v.paused) v.pause();
          v._mcShouldPlay = false;
        });
        item.querySelectorAll('[data-mc-embed] iframe').forEach(iframe => {
          iframe.src = 'about:blank';
        });
        // Restaurar poster en ítems que pasen a inactivos
        const poster = item.querySelector('[data-mc-poster]');
        if (poster) poster.classList.remove('is-hidden');
      });
    }

    _setActive(index, { play = true } = {}) {
      if (index < 0 || index >= this.items.length) return;
      if (index === this.activeIndex) return;

      this.activeIndex = index;
      const activeItem = this.items[index];
      if (!activeItem) return;

      const activeHandle = activeItem.dataset.mcItem;

      // Clases en items
      this.items.forEach((el, i) => el.classList.toggle('is-active', i === index));

      // Nav: clase + aria-current
      this.navItems.forEach(el => {
        el.classList.toggle('is-active', el.dataset.mcNav === activeHandle);
      });
      this.navLinks.forEach(link => {
        link.setAttribute('aria-current', link.dataset.mcNavLink === activeHandle ? 'true' : 'false');
      });

      this._syncMobileUIColors(activeItem);

      // Mover el track filmstrip (solo desktop)
      this._updateTrackPosition(index);

      // Reproducción
      if (play) {
        this._pauseAll();

        const video   = this._getVideoEl(activeItem);
        const embedEl = activeItem.querySelector('[data-mc-embed]');

        if (video && video.tagName === 'VIDEO') {
          this._playVideo(video, activeItem);
        } else if (embedEl) {
          this._activateEmbed(embedEl, activeItem);
        }
      }

      if (this.panelModal?.isOpen) {
        this.panelModal.syncColorsFromItem(activeItem);
      }
    }

    // ── Reproducción de <video> ──────────────────────────────────────────────

    _playVideo(video, item) {
      const poster = item.querySelector('[data-mc-poster]');

      video._mcShouldPlay = true;

      const doPlay = () => {
        if (!video._mcShouldPlay) return; // Fue pausado antes de poder reproducir
        video.play().catch(() => { /* autoplay bloqueado por política del navegador */ });
      };

      // Ocultar poster en cuanto el vídeo empieza a reproducirse de verdad
      video.addEventListener('playing', () => {
        poster?.classList.add('is-hidden');
      }, { once: true });

      if (video.readyState >= 2) {
        // El navegador ya tiene datos suficientes → reproducir de inmediato
        doPlay();
      } else {
        // Sin datos aún (preload="none"): cargar primero y esperar a canplay
        if (video.readyState === 0) video.load();
        video.addEventListener('canplay', doPlay, { once: true });
      }
    }

    // ── Gestión de embeds diferidos ──────────────────────────────────────────

    _activateEmbed(embedEl, item) {
      const embedUrl = embedEl.dataset.embedUrl;
      if (!embedUrl) return;

      let iframe = embedEl.querySelector('iframe');

      if (!iframe) {
        // Primera activación: crear el iframe
        iframe = document.createElement('iframe');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
        iframe.setAttribute('allowfullscreen', '');
        iframe.className = 'mc-home__video mc-home__video--embed';
        embedEl.appendChild(iframe);
      }

      // Asignar (o reasignar) la URL con autoplay
      iframe.src = embedUrl;

      // Ocultar poster cuando el iframe reporta que cargó
      const poster = item.querySelector('[data-mc-poster]');
      if (poster && !poster.classList.contains('is-hidden')) {
        iframe.addEventListener('load', () => {
          // Pequeño delay para que el vídeo empiece a reproducirse antes de ocultar el poster
          setTimeout(() => poster.classList.add('is-hidden'), 600);
        }, { once: true });

        // Fallback: ocultar después de 3 s si el iframe no dispara 'load'
        setTimeout(() => poster.classList.add('is-hidden'), 3000);
      }
    }

    // ── Carrusel filmstrip (desktop) ─────────────────────────────────────────

    _initDesktopCarousel() {
      if (!this.container || !this.track) return;

      // Wheel: avanzar/retroceder un item a la vez
      this.container.addEventListener('wheel', (e) => {
        e.preventDefault();
        if (this._wheelLock) return;

        const dir = e.deltaY > 0 ? 1 : -1;
        const next = this.activeIndex + dir;
        if (next < 0 || next >= this.items.length) return;

        this._wheelLock = true;
        this._setActive(next);
        setTimeout(() => { this._wheelLock = false; }, ANIM_MS + 80);
      }, { passive: false });

      // Flechas de teclado
      document.addEventListener('keydown', (e) => {
        if (this._isMobile()) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); this._setActive(this.activeIndex + 1); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); this._setActive(this.activeIndex - 1); }
      });
    }

    /**
     * Calcula el translateY del track para centrar el ítem activo en el contenedor.
     * Suma las alturas FINALES (después de transición) de todos los items.
     */
    _getTrackY(activeIndex) {
      const half = this.container.clientHeight / 2;
      let offset = 0;

      for (let i = 0; i < this.items.length; i++) {
        const h = (i === activeIndex) ? ACTIVE_H : THUMB_H;
        if (i < activeIndex) {
          offset += h + ITEM_GAP;
        } else if (i === activeIndex) {
          offset += h / 2;
        }
      }

      return half - offset;
    }

    _updateTrackPosition(activeIndex) {
      if (!this.track || this._isMobile()) return;
      const y = this._getTrackY(activeIndex);
      this.track.style.transform = `translateY(${y}px)`;
    }

    // ── Scroll snap (mobile) ─────────────────────────────────────────────────

    _initMobileScroll() {
      if (!this.container) return;

      this.container.addEventListener('scroll', () => {
        clearTimeout(this._scrollTimer);
        this._scrollTimer = setTimeout(() => {
          this._onMobileScrollEnd();
        }, 80);
      }, { passive: true });
    }

    _onMobileScrollEnd() {
      if (!this.container) return;
      const itemHeight = this.container.clientHeight;
      if (!itemHeight) return;

      const index = Math.round(this.container.scrollTop / itemHeight);
      const clampedIndex = Math.max(0, Math.min(index, this.items.length - 1));
      this._setActive(clampedIndex);
    }

    // ── Cambio de layout (mobile ↔ desktop) ──────────────────────────────────

    _handleBreakpointChange() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      if (this._isMobile()) {
        this._initMobileScroll();
      } else {
        this._initDesktopCarousel();
        this._updateTrackPosition(this.activeIndex);
      }

      // Reactivar el vídeo correcto para la nueva orientación/plataforma
      this._pauseAll();
      const current = this.items[this.activeIndex];
      if (current) {
        const video   = this._getVideoEl(current);
        const embedEl = current.querySelector('[data-mc-embed]');
        if (video && video.tagName === 'VIDEO') {
          this._playVideo(video, current);
        } else if (embedEl) {
          this._activateEmbed(embedEl, current);
        }
        this._syncMobileUIColors(current);
      } else {
        this._syncMobileUIColors(null);
      }
    }

    // ── Eventos ──────────────────────────────────────────────────────────────

    _bindOrientation() {
      this._mqOrientation.addEventListener('change', () => this._handleBreakpointChange());
      this._mqMobile.addEventListener('change', () => this._handleBreakpointChange());
    }

    // ── Click en vídeo: abrir modal y reflejar la URL real del metaobject ──────

    _bindExpandClick() {
      this.items.forEach((item, idx) => {
        const media = item.querySelector('[data-mc-item-media]');

        // Click en el área de vídeo:
        // - ítem inactivo (desktop) → activarlo como preview
        // - ítem activo / móvil → abrir fullscreen con URL compartible
        media?.addEventListener('click', (e) => {
          e.stopPropagation();
          if (!this._isMobile() && !item.classList.contains('is-active')) {
            this._setActive(idx);
            return;
          }

          if (item.classList.contains('is-expanded')) return;
          this._expandItem(item, idx);
        });
      });

      // ESC: cerrar drawer primero; si no hay drawer, cerrar modal
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (document.querySelector('.mc-drawer.is-open')) return;
        this._collapseExpanded();
      });
    }

    // ── Lógica del modal expandido ────────────────────────────────────────────

    _expandItem(item, idx, { updateHistory = true, replaceHistory = false } = {}) {
      this._collapseExpanded({ updateHistory: false });

      this._expandedOrigin = {
        parent: item.parentNode,
        nextSibling: item.nextSibling,
      };
      document.body.appendChild(item);

      item.classList.add('is-expanded');
      item.removeAttribute('aria-hidden');

      this._ensureModalPlayback(item);

      if (!item._mcDrawer) {
        item._mcDrawer = new ItemDrawerController(item);
      }

      document.body.style.overflow = 'hidden';
      this.section.setAttribute('aria-hidden', 'true');

      this._startModalTimer(item);
      this._bindModalControls(item, idx);

      if (updateHistory) {
        this._pushVideoUrl(item, { replace: replaceHistory });
      }
    }

    /** Oculta poster y garantiza reproducción del vídeo/embed en el modal */
    _ensureModalPlayback(item) {
      const poster = item.querySelector('[data-mc-poster]');
      if (poster) {
        poster.classList.add('is-hidden');
        poster.style.display = 'none';
      }

      // Quitar poster nativo de TODOS los <video> del ítem (desktop + mobile)
      this._stripNativePosters(item);

      const video   = this._getVideoEl(item);
      const embedEl = item.querySelector('[data-mc-embed]');

      if (video && video.tagName === 'VIDEO') {
        video._mcShouldPlay = true;
        video.muted = false;
        this._playVideo(video, item);
        return;
      }

      if (embedEl) {
        this._activateEmbed(embedEl, item);
        if (poster) poster.classList.add('is-hidden');
      }
    }

    _stripNativePosters(item) {
      item.querySelectorAll('video').forEach((v) => {
        if (v.hasAttribute('poster') && v._mcPosterAttr === undefined) {
          v._mcPosterAttr = v.getAttribute('poster');
          v.removeAttribute('poster');
        }
      });
    }

    _restoreVideoPoster(video) {
      if (!video || video._mcPosterAttr === undefined) return;
      video.setAttribute('poster', video._mcPosterAttr);
      delete video._mcPosterAttr;
    }

    _restoreAllVideoPosters(item) {
      item.querySelectorAll('video').forEach((v) => this._restoreVideoPoster(v));
    }

    _collapseExpanded({ updateHistory = true } = {}) {
      const expanded = document.querySelector('.mc-home__item.is-expanded');
      if (!expanded) return;

      if (
        updateHistory &&
        window.history.state?.mcHome === true &&
        window.history.state?.mcHomeExpandedHandle
      ) {
        if (window.history.state.mcHomeCanGoBack === false) {
          window.history.replaceState(null, '', window.history.state.mcHomeReturnUrl || this._homeUrl);
        } else {
          window.history.back();
          return;
        }
      }

      expanded._mcDrawer?.close();

      expanded.classList.remove('is-expanded');
      expanded.setAttribute('aria-hidden', 'true');

      // Devolver el ítem a su posición original en el track
      if (this._expandedOrigin?.parent) {
        const { parent, nextSibling } = this._expandedOrigin;
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(expanded, nextSibling);
        } else {
          parent.appendChild(expanded);
        }
      }
      this._expandedOrigin = null;

      const video = this._getVideoEl(expanded);
      if (video) video.muted = true;
      this._restoreAllVideoPosters(expanded);

      const poster = expanded.querySelector('[data-mc-poster]');
      if (poster) {
        poster.style.display = '';
        if (video && !video.paused) poster.classList.add('is-hidden');
      }

      document.body.style.overflow = '';
      this.section.removeAttribute('aria-hidden');
      this._stopModalTimer();
    }

    // ── Timer del modal ───────────────────────────────────────────────────────

    _startModalTimer(item) {
      this._stopModalTimer();
      const timeEl = item.querySelector('[data-mc-modal-time]');
      if (!timeEl) return;

      const video = this._getVideoEl(item);
      if (!video) return;

      const fmt = (s) => {
        const m  = Math.floor(s / 60);
        const ss = Math.floor(s % 60);
        const cs = Math.floor((s % 1) * 100);
        return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}:${String(cs).padStart(2, '0')}`;
      };

      this._modalTimerRaf = null;
      const tick = () => {
        if (!item.classList.contains('is-expanded')) return;
        timeEl.textContent = fmt(video.currentTime || 0);
        this._modalTimerRaf = requestAnimationFrame(tick);
      };
      this._modalTimerRaf = requestAnimationFrame(tick);
    }

    _stopModalTimer() {
      if (this._modalTimerRaf) {
        cancelAnimationFrame(this._modalTimerRaf);
        this._modalTimerRaf = null;
      }
    }

    // ── Controles del modal ───────────────────────────────────────────────────

    _bindModalControls(item, idx) {
      const video = this._getVideoEl(item);

      // Limpiar listeners previos clonando los botones
      const rebind = (selector, handler) => {
        const el = item.querySelector(selector);
        if (!el) return;
        const clone = el.cloneNode(true);
        el.parentNode.replaceChild(clone, el);
        clone.addEventListener('click', (e) => { e.stopPropagation(); handler(clone); });
        return clone;
      };

      // Play / Pause
      rebind('[data-mc-modal-play]', (btn) => {
        if (!video) return;
        if (video.paused) {
          video.play();
          btn.querySelector('[data-mc-modal-play-label]').textContent = 'Pause';
          btn.setAttribute('aria-label', 'Pausar');
        } else {
          video.pause();
          btn.querySelector('[data-mc-modal-play-label]').textContent = 'Play';
          btn.setAttribute('aria-label', 'Reproducir');
        }
      });

      // Mute / Unmute
      rebind('[data-mc-modal-mute]', (btn) => {
        if (!video) return;
        video.muted = !video.muted;
        const label = video.muted ? 'Sound off' : 'Sound on';
        btn.querySelector('[data-mc-modal-mute-label]').textContent = label;
        btn.setAttribute('aria-label', video.muted ? 'Activar sonido' : 'Silenciar');
      });

      // Fullscreen nativo (el elemento de vídeo)
      rebind('[data-mc-modal-fullscreen]', (btn) => {
        const target = item.querySelector('[data-mc-item-media]') || item;
        if (!document.fullscreenElement) {
          target.requestFullscreen?.().catch(() => {});
          btn.querySelector('[data-mc-modal-fullscreen-label]').textContent = 'Exit full';
        } else {
          document.exitFullscreen?.();
          btn.querySelector('[data-mc-modal-fullscreen-label]').textContent = 'Fullscreen';
        }
      });

      // PREV
      const prevBtn = item.querySelector('[data-mc-modal-prev]');
      if (prevBtn) {
        const prevClone = prevBtn.cloneNode(true);
        prevBtn.parentNode.replaceChild(prevClone, prevBtn);
        if (idx <= 0) {
          prevClone.disabled = true;
        } else {
          prevClone.addEventListener('click', (e) => {
            e.stopPropagation();
            item._mcDrawer?.close();
            this._collapseExpanded({ updateHistory: false });
            this._setActive(idx - 1);
            this._expandItem(this.items[idx - 1], idx - 1, { replaceHistory: true });
          });
        }
      }

      // NEXT
      const nextBtn = item.querySelector('[data-mc-modal-next]');
      if (nextBtn) {
        const nextClone = nextBtn.cloneNode(true);
        nextBtn.parentNode.replaceChild(nextClone, nextBtn);
        if (idx >= this.items.length - 1) {
          nextClone.disabled = true;
        } else {
          nextClone.addEventListener('click', (e) => {
            e.stopPropagation();
            item._mcDrawer?.close();
            this._collapseExpanded({ updateHistory: false });
            this._setActive(idx + 1);
            this._expandItem(this.items[idx + 1], idx + 1, { replaceHistory: true });
          });
        }
      }

      // EXIT / close (puede haber varios [data-mc-item-close] en el overlay)
      item.querySelectorAll('[data-mc-item-close]').forEach(btn => {
        const clone = btn.cloneNode(true);
        btn.parentNode.replaceChild(clone, btn);
        clone.addEventListener('click', (e) => {
          e.stopPropagation();
          this._collapseExpanded();
        });
      });
    }

    // ── Sincronización de URL del modal ───────────────────────────────────────

    _bindHistory() {
      window.addEventListener('popstate', this._popStateHandler);
    }

    _getHomeUrl() {
      const url = new URL(window.location.href);
      const pathHandle = this._getVideoHandleFromPath(url);
      if (pathHandle) {
        const segments = url.pathname.replace(/\/$/, '').split('/');
        segments.pop();
        url.pathname = segments.join('/') || '/';
      }
      return `${url.pathname}${url.search}`;
    }

    _getVideoHandleFromUrl() {
      const url = new URL(window.location.href);
      return (
        url.searchParams.get('video') ||
        decodeURIComponent(url.hash.replace(/^#/, '')) ||
        this._getVideoHandleFromPath(url)
      );
    }

    _getVideoHandleFromPath(url = new URL(window.location.href)) {
      const lastSegment = decodeURIComponent(url.pathname.replace(/\/$/, '').split('/').pop() || '');
      if (!lastSegment) return null;
      return this.items.some(el => el.dataset.mcItem === lastSegment) ? lastSegment : null;
    }

    _openInitialVideoFromUrl() {
      const handle = this._getVideoHandleFromUrl();
      if (!handle) return;

      const target = this.items.find(el => el.dataset.mcItem === handle);
      if (!target) return;

      const targetIndex = parseInt(target.dataset.mcIndex, 10);
      this._setActive(targetIndex);
      this._expandItem(target, targetIndex, { updateHistory: false });
      window.history.replaceState({
        mcHome: true,
        mcHomeExpandedHandle: handle,
        mcHomeReturnUrl: this._homeUrl,
        mcHomeCanGoBack: false,
      }, '', window.location.href);
    }

    _pushVideoUrl(item, { replace = false } = {}) {
      const url = item?.dataset?.mcUrl;
      const handle = item?.dataset?.mcItem;
      if (!url || !handle) return;

      const currentState = window.history.state;
      const state = {
        mcHome: true,
        mcHomeExpandedHandle: handle,
        mcHomeReturnUrl: currentState?.mcHomeReturnUrl || this._homeUrl,
        mcHomeCanGoBack: replace && currentState?.mcHome === true
          ? currentState.mcHomeCanGoBack !== false
          : true,
      };
      const method = replace ? 'replaceState' : 'pushState';
      window.history[method](state, '', url);
    }

    _onPopState(e) {
      const handle = e.state?.mcHome === true ? e.state.mcHomeExpandedHandle : null;

      if (!handle) {
        this._collapseExpanded({ updateHistory: false });
        return;
      }

      const target = this.items.find(el => el.dataset.mcItem === handle);
      if (!target) {
        this._collapseExpanded({ updateHistory: false });
        return;
      }

      const targetIndex = parseInt(target.dataset.mcIndex, 10);
      this._setActive(targetIndex);
      this._expandItem(target, targetIndex, { updateHistory: false });
    }

    _bindNavLinks() {
      this.navLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();

          const handle = link.dataset.mcNavLink;
          const target = this.items.find(el => el.dataset.mcItem === handle);
          if (!target) return;

          const targetIndex = parseInt(target.dataset.mcIndex, 10);

          if (this._isMobile()) {
            this.container?.scrollTo({
              top: targetIndex * this.container.clientHeight,
              behavior: 'smooth',
            });
          } else {
            // Desktop filmstrip: el track se mueve directamente via _setActive
            this._setActive(targetIndex);
          }
        });
      });
    }

    _bindShopifyEvents() {
      // Shopify theme editor: re-init al recargar la sección
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this.section)) this._reInit();
      });
    }

    _reInit() {
      this.destroy();
      if (this._isMobile()) {
        this._initMobileScroll();
      } else {
        this._initObserver();
      }
      this.activeIndex = -1;
      this._setActive(0);
    }

    // ── Destrucción ──────────────────────────────────────────────────────────

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      clearTimeout(this._scrollTimer);
      clearTimeout(this._scrollLockTimer);
      this._wheelLock = false;
      this._stopModalTimer();
      window.removeEventListener('popstate', this._popStateHandler);
      this.panelModal?.destroy();
      this.items.forEach((item) => item._mcDrawer?.destroy());
      this._collapseExpanded({ updateHistory: false });
      this._pauseAll();
    }
  }

  // ── Bootstrap ───────────────────────────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('[data-mc-section]').forEach(section => {
      if (!section._mcHome) {
        section._mcHome = new MassConstructionHome(section);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-mc-section]');
    if (!section) return;
    if (section._mcHome) section._mcHome.destroy();
    section._mcHome = new MassConstructionHome(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('[data-mc-section]');
    if (section && section._mcHome) {
      section._mcHome.destroy();
      delete section._mcHome;
    }
  });

})();
